import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider, Tooltip,
} from '@mui/material';
import { Home, Inbox, CheckCircle, ThumbDown, Refresh } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—';

const STATUS_COLOR = { Open: 'warning', Approved: 'success', Rejected: 'error', Canceled: 'default', Created: 'info' };

// Field names not worth showing in the generic document detail dump.
const HIDE = new Set(['_id', '__v', 'createdAt', 'updatedAt', 'createdBy', 'lines']);

const ApprovalsInbox = () => {
  const navigate = useNavigate();
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  const [open,    setOpen]    = useState(null);   // the entry being reviewed
  const [doc,     setDoc]     = useState(null);
  const [trail,   setTrail]   = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [acting,  setActing]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/approvals/pending`, { headers: hdrs() });
      setItems(data);
      setError('');
    } catch { setError('Failed to load approvals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openReview = async (entry) => {
    setOpen(entry); setComment(''); setDoc(null); setTrail([]); setDetailLoading(true);
    try {
      const [dRes, tRes] = await Promise.all([
        axios.get(`${API}/api/approvals/document/${entry.documentType}/${entry.documentId}`, { headers: hdrs() }),
        axios.get(`${API}/api/approvals/trail/${entry.documentType}/${entry.documentId}`, { headers: hdrs() }),
      ]);
      setDoc(dRes.data.document);
      setTrail(tRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load document');
    } finally { setDetailLoading(false); }
  };

  const act = async (action) => {
    if (action === 'reject' && !comment.trim()) { setError('A comment is required to reject'); return; }
    setActing(true); setError('');
    try {
      const { data } = await axios.post(
        `${API}/api/approvals/entries/${open._id}/${action}`, { comment }, { headers: hdrs() });
      setOpen(null);
      setSuccess(data.message || 'Done');
      setTimeout(() => setSuccess(''), 4000);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Action failed');
    } finally { setActing(false); }
  };

  const scalarFields = doc
    ? Object.entries(doc).filter(([k, v]) => !HIDE.has(k) && (typeof v !== 'object' || v === null))
    : [];

  if (loading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 6, textAlign: 'center' }}><CircularProgress /></Box>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Inbox color="primary" />
              <Typography variant="h5" color="primary">My Approvals</Typography>
              <Chip label={items.length} size="small" color={items.length ? 'warning' : 'default'} sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
              <Tooltip title="Dashboard">
                <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {items.length === 0 ? (
            <Typography color="text.secondary" py={3} align="center">
              Nothing is waiting for your approval right now.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Document</strong></TableCell>
                  <TableCell><strong>Reference</strong></TableCell>
                  <TableCell><strong>Details</strong></TableCell>
                  <TableCell><strong>From</strong></TableCell>
                  <TableCell><strong>Sent</strong></TableCell>
                  <TableCell align="right"><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(it => (
                  <TableRow key={it._id} hover sx={{ cursor: 'pointer' }} onClick={() => openReview(it)}>
                    <TableCell><Chip label={it.label} size="small" variant="outlined" /></TableCell>
                    <TableCell><strong>{it.documentNo}</strong></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {(it.summary?.lines || []).join(' · ')}
                      </Typography>
                    </TableCell>
                    <TableCell>{it.senderName || it.senderEmail}</TableCell>
                    <TableCell>{fmtDate(it.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); openReview(it); }}>
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review dialog */}
      <Dialog open={Boolean(open)} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        {open && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Inbox color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{open.label} — {open.documentNo}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    From {open.senderName || open.senderEmail} · Step {open.sequence}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {detailLoading ? (
                <Box textAlign="center" py={3}><CircularProgress /></Box>
              ) : (
                <>
                  {/* Document details */}
                  <Typography variant="subtitle2" color="primary" gutterBottom>Document</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mb: 1.5 }}>
                    {scalarFields.map(([k, v]) => (
                      <Box key={k} display="flex" gap={1}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130, fontWeight: 500 }}>{k}</Typography>
                        <Typography variant="body2">{String(v ?? '')}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Line items, if any */}
                  {Array.isArray(doc?.lines) && doc.lines.length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="primary" gutterBottom>Lines</Typography>
                      <Table size="small" sx={{ mb: 1.5 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            {Object.keys(doc.lines[0]).filter(k => k !== '_id').map(k => (
                              <TableCell key={k}><strong>{k}</strong></TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {doc.lines.map((ln, i) => (
                            <TableRow key={i}>
                              {Object.keys(doc.lines[0]).filter(k => k !== '_id').map(k => (
                                <TableCell key={k}>{String(ln[k] ?? '')}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {/* Approval trail */}
                  <Typography variant="subtitle2" color="primary" gutterBottom>Approval Trail</Typography>
                  <Table size="small" sx={{ mb: 1.5 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>Seq</strong></TableCell>
                        <TableCell><strong>Approver</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Comment</strong></TableCell>
                        <TableCell><strong>Acted</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trail.map(t => (
                        <TableRow key={t._id}>
                          <TableCell>{t.sequence}</TableCell>
                          <TableCell>{t.approverName || t.approverId} <Chip label={t.approverType} size="small" variant="outlined" sx={{ ml: 0.5, height: 18, fontSize: 10 }} /></TableCell>
                          <TableCell><Chip label={t.status} size="small" color={STATUS_COLOR[t.status] || 'default'} /></TableCell>
                          <TableCell><Typography variant="caption">{t.comment || '—'}</Typography></TableCell>
                          <TableCell><Typography variant="caption">{t.actedByName ? `${t.actedByName} · ${fmtDate(t.actedAt)}` : '—'}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <TextField
                    label="Comment (required to reject)" value={comment}
                    onChange={e => setComment(e.target.value)}
                    fullWidth size="small" multiline rows={2} sx={{ mt: 1 }} />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(null)} disabled={acting}>Close</Button>
              <Button color="error" variant="outlined" startIcon={<ThumbDown />}
                onClick={() => act('reject')} disabled={acting || detailLoading}>Reject</Button>
              <Button color="success" variant="contained" startIcon={<CheckCircle />}
                onClick={() => act('approve')} disabled={acting || detailLoading}>Approve</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ApprovalsInbox;
